import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import Header from '../../../components/Header/Header';
import SafeIframe from '../../../components/SafeIframe/SafeIframe';
import {
  fetchQuickSightEmbedUrl,
  fetchQuickSightDashboardsList,
} from '../../../actions/quicksightActions';
import './QuickSignt.css';
import { getPermissions } from '../../../utils/getPermissions';

/**
 * @typedef {Object} QuickSightDashboard
 * @property {string} dashboardId - Unique QuickSight dashboard identifier.
 * @property {string} [dashboardName] - Human-readable dashboard label.
 */

/**
 * @typedef {Object} QuickSightPicker
 * @property {?QuickSightDashboard[]} dashboards - The dashboards the user may open, or `null` until the list resolves.
 * @property {string} selectedDashboardId - The dashboard shown in the frame, or an empty string when none is chosen.
 */

/**
 * @typedef {Object} QuickSightPageState
 * @property {string} embedUrl - The embed URL rendered inside the iframe.
 * @property {string} error - The user-facing error shown when loading fails.
 * @property {boolean} loading - Whether the page is still resolving dashboard data.
 */

/**
 * Creates the page state used before any QuickSight data resolves, and while a dashboard embed is loading.
 *
 * @returns {QuickSightPageState}
 */
const createLoadingState = () => ({
  embedUrl: '',
  error: '',
  loading: true,
});
/**
 * Builds the page state for a successful dashboard embed load.
 *
 * @param {{embedUrl?: string}} result - Embed data from the embed endpoint.
 * @returns {QuickSightPageState}
 */
const createSuccessState = (result = {}) => ({
  embedUrl: result.embedUrl || '',
  error: '',
  loading: false,
});
/**
 * Builds the page state for an error outcome.
 *
 * @param {string} errorMessage - The message shown in the page error panel.
 * @returns {QuickSightPageState}
 */
const createErrorState = errorMessage => ({
  embedUrl: '',
  error: errorMessage,
  loading: false,
});

/**
 * Extracts a user-friendly error message from a QuickSight API error response,
 * falling back to a generic message if necessary.
 *
 * @param {Error & {userMessage?: string}} error - The error object returned by QuickSight API calls.
 * @returns {string} The extracted user-friendly error message.
 */
const getErrorMessage = error => {
  if (error?.userMessage) {
    return error.userMessage;
  }

  if (error?.message) {
    return error.message;
  }

  console.log('Unexpected error loading SignSense embed URL', error);
  console.trace();

  return 'SignSense could not be loaded.';
};

/**
 * Returns the best available label for a dashboard option.
 *
 * @param {QuickSightDashboard} dashboard - Dashboard metadata to render.
 * @returns {string}
 */
const getDashboardLabel = dashboard =>
  dashboard.dashboardName || dashboard.dashboardId || 'Unknown';
/**
 * Renders the dashboard picker shown when the user can choose between multiple dashboards.
 *
 * @param {Object} params - Selector rendering parameters.
 * @param {QuickSightDashboard[]} params.dashboards - Available dashboards for the current user.
 * @param {string} params.selectedDashboardId - Currently selected dashboard identifier.
 * @param {(event: React.ChangeEvent<HTMLSelectElement>) => void} params.onChange - Change handler for the selector.
 * @returns {JSX.Element}
 */
const renderDashboardSelector = ({ dashboards, selectedDashboardId, onChange }) => (
  <div className="quicksight-dashboard-selector mb-4" data-test="quicksight-dashboard-selector">
    <label
      className="quicksight-dashboard-selector-label block mb-1"
      htmlFor="quicksight-dashboard-select"
    >
      Dashboard
    </label>
    <select
      id="quicksight-dashboard-select"
      className="quicksight-dashboard-select"
      data-test="quicksight-dashboard-select"
      aria-label="Select QuickSight dashboard"
      onChange={onChange}
      value={selectedDashboardId}
    >
      <option value="">Select a dashboard</option>
      {dashboards.map(dashboard => (
        <option key={dashboard.dashboardId} value={dashboard.dashboardId}>
          {getDashboardLabel(dashboard)}
        </option>
      ))}
    </select>
  </div>
);

/**
 * Renders the current QuickSight page content for the resolved loading state.
 *
 * @param {QuickSightPageState} viewState - The current page state.
 * @returns {JSX.Element}
 */
const renderContent = viewState => {
  const { embedUrl, error, loading } = viewState;

  if (loading) {
    return <p data-test="quicksight-loading">Loading SignSense dashboard...</p>;
  }

  if (error) {
    return (
      <div data-test="quicksight-error">
        <h2>SignSense unavailable</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <SafeIframe
      dataTest="quicksight-iframe"
      src={embedUrl}
      title="SignSense Dashboard"
      className="quicksight-iframe"
    />
  );
};

/**
 * Loads the current user's QuickSight dashboards and renders the active selection inside a safe iframe.
 *
 * Behavior:
 * - On mount the page fetches the dashboard list. If the list is empty or the fetch fails, it sets an
 *   error state. Otherwise it stores the list together with its first entry as the selection and loads
 *   that dashboard, so the page opens it by itself: the API lists the dashboard of the server's own
 *   lane first. The list and the selection are one piece of state, and the embed URL is fetched in
 *   the same async chain, which avoids the React 16 unbatched-update race that occurs when separate
 *   `setState` calls feed a chained effect from an async context outside an event handler.
 * - When the user picks another dashboard, the change handler loads it the same way. The selector,
 *   shown when the list holds more than one dashboard, stays visible so the user can switch
 *   dashboards without leaving the page.
 * - Embed requests are counted, and only the answer to the newest one is shown: an answer that
 *   arrives after the user picked another dashboard, or after the page is gone, is dropped.
 * - The selection prompt is derived from `needsSelection` in the render body rather than from
 *   `viewState`, so no extra state update is needed to show it.
 *
 * @param {{auth: object}} props
 * @returns {JSX.Element}
 */
function QuickSightPage(props) {
  const [picker, setPicker] = useState(
    /** @type {QuickSightPicker} */ ({ dashboards: null, selectedDashboardId: '' }),
  );
  const [viewState, setViewState] = useState(createLoadingState);
  const newestEmbedRequest = useRef(0);
  const { dashboards, selectedDashboardId } = picker;
  const showDashboardSelector = dashboards !== null && dashboards.length > 1;
  const needsSelection = showDashboardSelector && !selectedDashboardId;

  /**
   * Fetches the embed URL of a dashboard and shows it, unless a newer request was made meanwhile.
   *
   * @param {string} dashboardId - The dashboard to open.
   * @returns {Promise<void>}
   */
  const openDashboard = async dashboardId => {
    newestEmbedRequest.current += 1;
    const embedRequest = newestEmbedRequest.current;

    setViewState(createLoadingState());

    try {
      const result = await fetchQuickSightEmbedUrl(dashboardId);

      if (embedRequest === newestEmbedRequest.current) {
        setViewState(createSuccessState(result));
      }
    } catch (error) {
      if (embedRequest === newestEmbedRequest.current) {
        setViewState(createErrorState(getErrorMessage(error)));
      }
    }
  };

  /**
   * Updates the selected dashboard id and loads that dashboard.
   *
   * @param {React.ChangeEvent<HTMLSelectElement>} event - Selector change event.
   * @returns {Promise<void>}
   */
  const handleDashboardSelectionChange = async event => {
    const { value } = event.target;

    setPicker(current => ({ ...current, selectedDashboardId: value }));

    if (!value) {
      return;
    }

    if (!dashboards.some(dashboard => dashboard.dashboardId === value)) {
      newestEmbedRequest.current += 1;
      setViewState(createErrorState('The selected QuickSight dashboard could not be found.'));

      return;
    }

    await openDashboard(value);
  };

  // Mount-only. Fetches the dashboard list, selects its first entry and loads it.
  useEffect(() => {
    if (!permissions.atLeastTierEnterprise) {
      // If the user is not enterprise-tier, we don't fetch dashboards or embed URLs.
      return undefined;
    }

    let isCurrent = true;

    /**
     * Fetches the dashboard list, selects its first entry and loads that dashboard in the same
     * async chain.
     *
     * @returns {Promise<void>}
     */
    const init = async () => {
      let list;

      try {
        const data = await fetchQuickSightDashboardsList();

        if (!isCurrent) {
          return;
        }

        list = Array.isArray(data?.dashboards) ? data.dashboards : [];
      } catch (error) {
        console.error('Failed to load SignSense dashboards list', error);

        if (!isCurrent) {
          return;
        }

        setViewState(createErrorState(getErrorMessage(error)));

        return;
      }

      if (!list.length) {
        console.warn('No SignSense dashboards found for this user');
        setViewState(createErrorState('No SignSense dashboards are available for this user.'));

        return;
      }

      // Single setState: the list and its first entry as the selection.
      setPicker({ dashboards: list, selectedDashboardId: list[0].dashboardId });
      await openDashboard(list[0].dashboardId);
    };

    init();

    return () => {
      isCurrent = false;
      // No request is the newest any more, so an embed answer still on its way is dropped.
      newestEmbedRequest.current = -1;
    };
  }, []);

  const { auth, constants } = props;
  const permissions = getPermissions(auth);

  if (!permissions.atLeastTierEnterprise) {
    return (
      <div className="page-structure">
        <Helmet title="SignSense" />
        <Header />
        <div className="xl:ml-16 xl:mr-16 ml-8 mr-8 mt-8 quicksignt-tier-parent">
          <a
            href={constants.signsense_feature_link}
            rel="noopener noreferrer"
            target="_blank"
            className="message"
          >
            <img
              src="/assets/images/signsense/tier_limit.png"
              alt="SignSense is an Enterprise feature"
              draggable={false}
            />
          </a>
          <img
            src="/assets/images/signsense/demo1.png"
            alt="SignSense is an Enterprise feature demo 1"
            draggable={false}
            className="demo"
          />
          <img
            src="/assets/images/signsense/demo2.png"
            alt="SignSense is an Enterprise feature demo 2"
            draggable={false}
            className="demo"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="page-structure">
      <Helmet title="SignSense" />
      <Header />
      <div className="xl:ml-16 xl:mr-16 ml-8 mr-8 mt-8">
        <div className="page-panels">
          <div className="page-content page-home">
            {showDashboardSelector
              ? renderDashboardSelector({
                  dashboards,
                  selectedDashboardId,
                  onChange: handleDashboardSelectionChange,
                })
              : null}
            {needsSelection ? (
              <p data-test="quicksight-selection-prompt">Select a dashboard to load.</p>
            ) : (
              renderContent(viewState)
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

QuickSightPage.propTypes = {
  auth: PropTypes.object.isRequired,
  constants: PropTypes.shape({
    signsense_feature_link: PropTypes.string,
  }).isRequired,
};

const mapStateToProps = state => ({
  auth: state.auth,
  constants: state.constants.data,
});

export default withRouter(connect(mapStateToProps)(QuickSightPage));
